import { createFeatureSelector, createSelector } from '@ngrx/store';
import { FEATURE_KEY, State } from './consultation.reducer';
import * as _ from 'lodash';
import { QuestionsCatalog } from '@care-portals/data';

// Lookup the 'Consultation' feature state managed by NgRx
export const getConsultationState = createFeatureSelector<State>(FEATURE_KEY);

export const getFormsState = createSelector(
  getConsultationState,
  (state: State) => state.forms
);

export const getAnswersState = createSelector(
  getConsultationState,
  (state: State) => state.answers
);

export const getSelectedFormType = createSelector(getFormsState, forms => {
  return forms.required.length > 0 ? forms.required[forms.selectedIndex] : null;
});

export const isLastForm = createSelector(
  getFormsState,
  forms => forms.selectedIndex === forms.required.length - 1
);

export const getConsultationLoading = createSelector(
  getConsultationState,
  (state: State) => state.loading
);

export const getImageUploading = createSelector(
  getConsultationState,
  (state: State) => state.uploading
);

export const getConsultationSaving = createSelector(
  getConsultationState,
  (state: State) => state.saving
);

export const getConsultationBusy = createSelector(
  getConsultationState,
  (state: State) => state.saving || state.loading || state.uploading
);

export const getConsultationError = createSelector(
  getConsultationState,
  (state: State) => state.error
);

export const getQuestionsState = createSelector(
  getConsultationState,
  (state: State) => state.questions
);

export const getRequiredQuestions = createSelector(
  getQuestionsState,
  questions => questions.required
);

export const getQuestionStack = createSelector(
  getQuestionsState,
  questions => questions.questionStack
);

export const getQuestionCounter = createSelector(getQuestionsState, questions =>
  questions.qs.join(' - ')
);

export const getFormCompleted = createSelector(
  getConsultationState,
  state => state.completed
);

export const getNextQuestionType = createSelector(
  getQuestionsState,
  questions => questions.nextQuestion
);

// need to update
export const getSelectedQuestionType = createSelector(
  getRequiredQuestions,
  getQuestionStack,
  (required, questionStack) =>
    questionStack.length > 0
      ? questionStack[questionStack.length - 1]
      : required[0]
);

export const getActiveQuestionHistory = createSelector(
  getQuestionsState,
  questions => questions
);

export const getSelectedQuestionModel = createSelector(
  getSelectedQuestionType,
  field => {
    if (typeof field === 'string') {
      return QuestionsCatalog.find(model => field === model.field);
    } else if (field?.key) {
      return QuestionsCatalog.find(model => field.key === model.field);
    }
  }
);

export const getSelectedAnswer = createSelector(
  getAnswersState,
  getSelectedQuestionType,
  (answers, selectedQuestionType) =>
    Object.assign({}, answers[selectedQuestionType]) || {}
);

export const getFiles = createSelector(
  getConsultationState,
  (state: State) => state.files || {}
);

export const getChangedFiles = createSelector(
  getConsultationState,
  (state: State) => state.changedFiles || {}
);

export const getDraftConsultation = createSelector(
  getConsultationState,
  getSelectedFormType,
  (state: State, type: string) =>
    state.draftConsults.find(consult => consult.type === type)
);

export const orderId = createSelector(
  getConsultationState,
  (state: State) => state.orderId || null
);

export const getRedirectUrl = createSelector(
  getConsultationState,
  (state: State) => state.redirectUrl || null
);

/** Pathwai hand-off URL, or null when nothing has been migrated for this org. */
export const getPathwaiHandoffUrl = createSelector(
  getConsultationState,
  (state: State) => state.pathwaiHandoffUrl || null
);

export const isLastQuestion = createSelector(
  getRequiredQuestions,
  getSelectedQuestionModel,
  (required, selectedQuestion) => {
    let last: any = required;
    if (selectedQuestion?.isLast) return true;

    while (last && typeof last === 'object' && last.length > 0) {
      last = _.last(last);
    }
    return last === selectedQuestion?.field;
  }
);

export const isFirstQuestion = createSelector(
  getQuestionStack,
  questionStack => questionStack.length === 1
);
